/**
 * district.aiAnalysisSettingsId から M3 出力フォーマット (AiOutputFormat) を解決する React hook
 * (Sprint BG-DataSettings 18、GAP U-L10 close)。
 *
 * 旧 v1 では `useEffect` 内で `loadAiOutputFormat()` を直接呼ぶ pattern が AnalysisLayout 等で
 * 散在していたが、v2 では本 hook に集約し、分析アプリ全体で共有可能にする。
 *
 * 踏襲点:
 *   - settingsId が null/undefined / 取得失敗時は format:null + loading:false で fallback
 *   - module-scope cache (aiFormatLoader 側) でセッション中の再 fetch を回避
 *
 * 変更点 (v2):
 *   - hook 化 (旧は plain async 関数を effect で呼ぶ pattern)
 *   - settings/format 両方を expose (旧 helper を経由した場合と同じ表面)
 *
 * 関連: useAnalysisDisplayConfig (M5 駆動) と並列で使用される想定。
 */
import { useEffect, useState } from 'react';
import type { AiAnalysisSettings, AiOutputFormat } from '@citizen-walk/shared-types';
import { loadAiOutputFormat } from './aiFormatLoader';

export interface UseOutputFormatResult {
  settings: AiAnalysisSettings | null;
  format: AiOutputFormat | null;
  loading: boolean;
}

export function useOutputFormat(
  settingsId: string | null | undefined,
  /** 地区の出力フォーマットの差し替え (docs/29 §5-1)。 */
  outputFormatIdOverride?: string | null,
): UseOutputFormatResult {
  const [state, setState] = useState<UseOutputFormatResult>({
    settings: null,
    format: null,
    loading: Boolean(settingsId),
  });

  useEffect(() => {
    if (!settingsId) {
      setState({ settings: null, format: null, loading: false });
      return;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));
    loadAiOutputFormat(settingsId, outputFormatIdOverride)
      .then((result) => {
        if (cancelled) return;
        setState({ settings: result.settings, format: result.format, loading: false });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ settings: null, format: null, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [settingsId, outputFormatIdOverride]);

  return state;
}
